import type { Logger } from "@expert/logging";
import type { GaiaPayloadConfigs, OrchestratorInvokeMessagePayload } from "@expert/shared-types";

import { useReactAnalytics } from "@asurion-core/eventualize-react";
import { sendMetrics } from "@expert/cloudwatch-metrics";
import { type TimelineFeatures, useFeatures } from "@expert/features";
import {
    type GaiaWebSocketResponse,
    fallbackGaiaPayloadProps,
    gaiaWsEventBus,
    getExperimentName,
    getFallbackPartner,
    getSupportedLineOfBusiness,
    useGaiaStore,
    useGaiaWebsocket,
} from "@expert/gaia";
import { trackFullStoryEvent } from "@expert/monitoring";
import { useMemo } from "react";

import type { RetryPayload, SendMessageProps } from "../shared-types";

import { addMessage, sendFailureMetric, useTimelineStore } from "../state";

const ORCHESTRATOR_RESPONSE_TIMEOUT_MS = 20_000;

export function setupOrchestratorTimeout(
    correlationId: string,
    sessionId: string,
    logger: Logger,
    expertMessageId: string,
    retryData: RetryPayload,
    appName?: string,
    startTime?: number,
): () => void {
    let cleanedUp = false;

    const emitRoundTripDuration = (outcome: "success" | "error" | "timeout") => {
        if (startTime === undefined || appName === undefined) {
            return;
        }
        void sendMetrics([
            {
                metrics: [{ name: "Duration", value: Math.round(performance.now() - startTime), unit: "ms" }],
                dimensions: { actionName: "gaia.orchestrator_invoke_response", gaiaAppName: appName, outcome },
            },
        ]);
    };

    const cleanup = () => {
        if (cleanedUp) return;
        cleanedUp = true;
        unsubSuccess();
        unsubError();
        clearTimeout(timer);
    };

    const unsubSuccess = gaiaWsEventBus.on("gaia_ws_orchestrator-invoke-success", (response: GaiaWebSocketResponse) => {
        if (response.correlationId !== correlationId) return;
        emitRoundTripDuration("success");
        cleanup();
        useTimelineStore.getState().updateMessage(sessionId, expertMessageId, {
            metadata: { retryPayload: undefined },
        });
    });

    const unsubError = gaiaWsEventBus.on("gaia_ws_orchestrator-invoke-error", (response: GaiaWebSocketResponse) => {
        if (response.correlationId !== correlationId) return;
        emitRoundTripDuration("error");
        cleanup();
    });

    const timer = setTimeout(() => {
        emitRoundTripDuration("timeout");
        cleanup();
        logger.warn({ correlationId, sessionId }, "GAIA orchestrator response timed out");
        sendFailureMetric("botFailure");
        useTimelineStore.getState().updateMessage(sessionId, expertMessageId, {
            metadata: { retryPayload: retryData },
        });
    }, ORCHESTRATOR_RESPONSE_TIMEOUT_MS);

    return cleanup;
}

export const useSendExpertAssistMessage = (loggerProp: Logger, gaiaPayloadProps: GaiaPayloadConfigs) => {
    const websocketObj = useGaiaWebsocket();
    const { subscriptionStatus } = useGaiaStore();
    const { dispatcher } = useReactAnalytics();
    const {
        features: { isGroundedSearchExperimentEnabled, isSalesFAQEnabled },
    } = useFeatures<TimelineFeatures>();
    const { expertId, currentSessionId: sessionId, getTimelineState } = useTimelineStore();
    const { callSid, chatId, lob, partner } = getTimelineState(sessionId);

    const logger = useMemo(() => loggerProp.child({ module: "useSendExpertAssistMessage" }), [loggerProp]);

    const sendMessage = async ({ id, metadata, text, type, source }: SendMessageProps) => {
        const messageId = id ?? crypto.randomUUID();
        const correlationId = crypto.randomUUID();
        const formattedPartnerForGaia = getFallbackPartner({ partner });
        const { areExperimentsEnabled, appName } =
            gaiaPayloadProps ?? fallbackGaiaPayloadProps(chatId ? "chat" : "voice");
        const experimentName = areExperimentsEnabled
            ? getExperimentName({
                  isGroundedSearchExperimentEnabled,
                  isSalesFAQEnabled,
                  lob,
              })
            : undefined;

        const lineOfBusiness = getSupportedLineOfBusiness({ partner: formattedPartnerForGaia, lob });
        const payload: OrchestratorInvokeMessagePayload = {
            action: "orchestrator-invoke",
            appName: appName,
            message: text,
            messageId,
            messageType: "Search",
            partner: formattedPartnerForGaia,
            sessionId: chatId ?? callSid ?? sessionId,
            sessionGroupId: sessionId,
            userId: expertId,
            userType: "expert",
            correlationId,
            experimentName,
            lineOfBusiness,
        };

        const messageText = metadata?.helpMeNowText ?? text;
        addMessage(sessionId, {
            id: messageId,
            text: messageText,
            type,
            isUnread: false,
            metadata,
            status: "active",
        });

        const retryData: RetryPayload = {
            text,
            type,
            source,
            metadata,
        };

        const analyticsInfo = {
            expertAssistAppName: "expert-assist",
            expertAssistExperimentName: experimentName,
            partner,
            messageId,
            messageType: type,
            source,
        };

        if (!websocketObj) {
            logger.error({ payload, websocketObj }, "There is no websocket obj");
            sendFailureMetric("connectionFailure");
            useTimelineStore.getState().updateMessage(sessionId, messageId, {
                metadata: { retryPayload: retryData },
            });
            return;
        }

        if (subscriptionStatus[sessionId] !== "subscribed") {
            logger.warn("Expert session not subscribed, subscribing before sending message");
            try {
                await websocketObj.subscribeSessionToGaia({
                    sessionId,
                    chatId,
                    callSid,
                    partner: formattedPartnerForGaia,
                    sendJsonMessage: websocketObj.sendJsonMessage,
                    logger,
                });
            } catch (error) {
                logger.error({ error }, "Failed to subscribe session to GAIA before sending message");
                sendFailureMetric("connectionFailure");
                useTimelineStore.getState().updateMessage(sessionId, messageId, {
                    metadata: { retryPayload: retryData },
                });
                return;
            }
        }
        const startTime = performance.now();
        websocketObj.sendJsonMessage(payload);
        setupOrchestratorTimeout(correlationId, sessionId, logger, messageId, retryData, appName, startTime);
        void dispatcher.dispatchBusinessEvent("MessageSent", {
            senderType: "expert",
            ...analyticsInfo,
        });
        logger.info({ payload }, "Message sent to gaia");
        trackFullStoryEvent("ExpertAssistMessageSent", {
            sessionId,
            callSid,
            chatId,
            ...analyticsInfo,
        });
    };
    return sendMessage;
};
